/* eslint-disable no-underscore-dangle */
import { useReducer, useRef } from 'react';
import {
  createFieldState,
  initializeFieldGroups,
} from './helpers';
import createReducer from './reducer';

export default function useFormControls(controlsArray, options = {}) {
  /* initialState is derived from the controlsArray
  controlsArray is an array of objects with the shape:
    {fieldName: string; !! Must match the 'name' property of the form field !!
    value: string | number | boolean,
    validators: []
    sanitizers: []}
  options.initialStateOverride is an optional object that contains the key-value pairs
    to initialize the state of the fields.
  options.fieldGroups is an optional object that defines repeating field groups:
    {
      groupName: {
        schema: [...controlsArray format...],
        minInstances: 1,
        maxInstances: 5,
        initialData: [{...}, {...}] // optional initial instances
      }
    }
    */
  const { initialStateOverride = {}, fieldGroups = {} } = options;

  // Store field group configs for later use
  const fieldGroupsRef = useRef(fieldGroups);

  // Initialize regular fields
  const initialState = controlsArray.reduce(
    (stateObject, control) => {
      const controlValue = initialStateOverride[control.fieldName] ?? control.value;
      const fields = {
        ...stateObject,
        [control.fieldName]: createFieldState(control, controlValue),
      };
      return fields;
    },
    {},
  );

  // Initialize field groups
  const { initialGroupInstances, groupFields } = initializeFieldGroups(
    fieldGroups,
    createFieldState,
  );

  // Merge group fields into initial state
  Object.assign(initialState, groupFields);

  // Create reducer
  const reducer = createReducer(initialState, initialGroupInstances);

  // dispatch for this reducer is private to the hook and should be wrapped by functions below.
  const [state, dispatch] = useReducer(
    reducer,
    { fields: initialState, groupInstances: initialGroupInstances },
  );

  const { fields } = state;
  const { groupInstances } = state;

  const getFormValues = () => {
    const formValues = {};
    Object.keys(fields).forEach((fieldName) => {
      formValues[fieldName] = fields[fieldName].value;
    });
    return formValues;
  };

  // validateField is an event handler that validates and sets the value of a field.
  // Provide it to onChange or onBlur (or any other event) to validate during that event.
  const validateField = (event) => {
    // eslint-disable-next-line object-curly-newline
    const { name: fieldName, value, type, checked } = event.target; // destructure the event.
    if (!fields[fieldName]) { // Confirm that the field exists and throw an error if not.
      throw new Error(`Error in setField: '${fieldName}' is not a valid field`);
    }
    let realValue = value;
    if (type === 'checkbox') {
      // if the field is a checkbox, set the value to checked
      realValue = checked;
    } else {
      // otherwise, set the value to the value of the event
      realValue = fields[fieldName].sanitizers.reduce(
        (sanitized, sanitizer) => sanitizer(sanitized) ?? sanitized, // sanitized == accumulator
        value, // starting value
      );
    }

    const { validators } = fields[fieldName]; // Get the validators array.
    const errors = validators.reduce( // Iterate over the validators array.
      (existingErrors, validator) => { // existingErrors is the accumulator array.
        const error = validator(realValue); // Run validators.
        if (error) { // If an error is returned, add it to the accumulator array.
          return [...existingErrors, error];
        }
        return existingErrors;
      }, [],
    );
    dispatch({ // call dispatch to update the form in state
      type: 'SET_FIELD',
      payload: {
        fieldName,
        value: {
          value: realValue,
          touched: true,
          dirty: !fields[fieldName].clean_value
            || (fields[fieldName].clean_value && realValue !== fields[fieldName].clean_value),
          valid: errors.length === 0,
          errors,
        },
      },
    });
  };
  // setField is an onChange event handler that updates the value of the field without validating.
  const setField = (event) => {
    // eslint-disable-next-line object-curly-newline
    const { name: fieldName, value, type, checked } = event.target; // destructure the event.
    if (!fields[fieldName]) { // confirm that the field exists and throw an error if not.
      throw new Error(`Error in setField: '${fieldName}' is not a valid field`);
    }
    const realValue = type === 'checkbox' ? checked : value;
    dispatch({
      type: 'SET_FIELD', // update the value of the form field to value, or checked if checkbox.
      payload: {
        fieldName,
        value: {
          value: realValue,
          touched: true,
          dirty: !fields[fieldName].clean_value
            || (fields[fieldName].clean_value && realValue !== fields[fieldName].clean_value),
          errors: [],
        },
      },
    });
  };

  // setFieldWithError is an onChange event handler that updates the value of
  // the field without validating and sets a feild's error message.
  const setFieldWithError = (event, error = null) => {
    // eslint-disable-next-line object-curly-newline
    const { name: fieldName, value, type, checked } = event.target; // destructure the event.
    if (!fields[fieldName]) { // confirm that the field exists and throw an error if not.
      throw new Error(`Error in setFieldWithError: '${fieldName}' is not a valid field`);
    }
    const realValue = type === 'checkbox' ? checked : value;
    const validValue = error === null;
    const errorsValue = validValue ? [] : [error];

    dispatch({
      // call dispatch to update the form in state
      type: 'SET_FIELD',
      payload: {
        fieldName,
        value: {
          value: realValue,
          touched: true,
          dirty: !fields[fieldName].clean_value
            || (fields[fieldName].clean_value && realValue !== fields[fieldName].clean_value),
          valid: validValue,
          errors: errorsValue,
        },
      },
    });
  };

  const setFieldValue = (fieldName, value) => {
    setField({
      target: {
        name: fieldName,
        value,
      },
    });
  };

  // setValidators allows us to change validations dynamically
  // (e.g., selecting one option sets another field to required).
  const setValidators = (fieldName, validators) => {
    if (!fields[fieldName]) { // confirm that the field exists and throw an error if not.
      throw new Error(`Error in setValidators: '${fieldName}' is not a valid field`);
    }
    dispatch({
      type: 'SET_VALIDATORS',
      payload: {
        fieldName,
        validators,
      },
    });
  };

  const setError = (fieldName, error = null) => {
    if (!fields[fieldName]) {
      // confirm that the field exists and throw an error if not.
      throw new Error(`Error in setError: '${fieldName}' is not a valid field`);
    }
    const validValue = error === null;
    const errorsValue = validValue ? [] : [error];
    dispatch({
      // call dispatch to update the form in state
      type: 'SET_FIELD',
      payload: {
        fieldName,
        value: {
          value: fields[fieldName].value,
          touched: true,
          valid: validValue,
          errors: errorsValue,
        },
      },
    });
  };

  const setErrors = (formErrors, callback = (e) => e.message) => {
    const newState = { ...fields };
    Object.keys(formErrors).forEach((field) => {
      const fieldErrors = formErrors[field];
      const messages = fieldErrors.map(callback);
      newState[field] = {
        ...fields[field],
        errors: messages,
        valid: false,
        touched: true,
      };
    });
    dispatch({
      type: 'SET_FORM',
      payload: newState,
    });
  };

  // resetField wraps dispatch to reset a field to its initial state
  const resetField = (fieldName) => {
    dispatch({
      type: 'RESET_FIELD',
      payload: { fieldName },
    });
  };
  // resetForm wraps dispatch to reset the whole form to its initial state
  const resetForm = () => {
    dispatch({
      type: 'INITIALIZE',
      payload: { fields: initialState, groupInstances: initialGroupInstances },
    });
  };

  const updateForm = (data) => {
    // update the values of the form fields by finding the corresponding field in data
    // and setting the value to the value in data
    const newState = {};
    Object.keys(fields).forEach((fieldName) => {
      newState[fieldName] = {
        ...fields[fieldName],
        clean_value: data[fieldName] ?? '',
        value: data[fieldName] ?? fields[fieldName].value,
        dirty: false,
        errors: [], // todo: this might warrant some regression testing
        valid: !fields[fieldName].validators.length
          || fields[fieldName].validators.reduce(
            (valid, validator) => valid && !validator(data[fieldName]),
            true,
          ),
      };
    });
    dispatch({
      type: 'SET_FORM',
      payload: newState,
    });
  };

  // getErroringFieldNames updates validity in state & returns array of field names with errors.
  const getErroringFieldNames = () => {
    const erroringFieldNames = [];
    Object.keys(fields).forEach((fieldName) => { // iterate over all the keys in state
      const { validators } = fields[fieldName]; // get the validators array
      let { errors } = fields[fieldName];
      // TODO: change this back to setting errors to validators.reduce, and stop including previous
      // errors. This change was made to accomodate errors being set on fields that did NOT come
      // from a validator. Moving forward we want all field errors to come from validators.
      errors = errors.concat(validators.reduce( // iterate over the validators array
        (existingErrors, validator) => { // existingErrors is the accumulator array
          const error = validator(fields[fieldName].value); // run validators
          if (error) { // if an error is returned, add it to the accumulator array
            return [...existingErrors, error];
          }
          return existingErrors;
        }, [],
      ));
      // todo: related to above concat and can also be removed when that is fixed
      errors = [...new Set(errors)]; // select distinct
      dispatch({
        type: 'SET_FIELD',
        payload: {
          fieldName,
          value: {
            ...fields[fieldName],
            touched: true,
            valid: errors.length === 0,
            errors,
          },
        },
      });
      if (errors.length) erroringFieldNames.push(fieldName);
    });
    return erroringFieldNames;
  };

  const validateForm = () => {
    return getErroringFieldNames().length === 0;
  };

  // New methods for dynamic field groups
  const addGroupInstance = (groupName) => {
    const groupConfig = fieldGroupsRef.current[groupName];
    if (!groupConfig) {
      throw new Error(`Field group '${groupName}' is not defined`);
    }

    const currentInstances = groupInstances[groupName] || [];
    if (currentInstances.length >= groupConfig.maxInstances) {
      throw new Error(`Cannot add more than ${groupConfig.maxInstances} instances of ${groupName}`);
    }

    const instanceId = crypto.randomUUID();
    dispatch({
      type: 'ADD_GROUP_INSTANCE',
      payload: {
        groupName,
        instanceId,
        schema: groupConfig.schema,
      },
    });

    return instanceId;
  };

  const removeGroupInstance = (groupName, instanceId) => {
    const groupConfig = fieldGroupsRef.current[groupName];
    if (!groupConfig) {
      throw new Error(`Field group '${groupName}' is not defined`);
    }

    const currentInstances = groupInstances[groupName] || [];
    if (currentInstances.length <= groupConfig.minInstances) {
      throw new Error(`Cannot remove below ${groupConfig.minInstances} instances of ${groupName}`);
    }

    dispatch({
      type: 'REMOVE_GROUP_INSTANCE',
      payload: { groupName, instanceId },
    });
  };

  const getGroupInstances = (groupName) => {
    return groupInstances[groupName] || [];
  };

  const getGroupInstanceValues = (groupName, instanceId) => {
    const instanceValues = {};
    Object.keys(fields).forEach((fieldName) => {
      const field = fields[fieldName];
      if (field._groupName === groupName && field._groupId === instanceId) {
        instanceValues[field._fieldKey] = field.value;
      }
    });
    return instanceValues;
  };

  const getGroupValues = (groupName) => {
    const instances = groupInstances[groupName] || [];
    return instances.map((instanceId) => getGroupInstanceValues(groupName, instanceId));
  };

  const getGroupFields = (groupName, instanceId) => {
    const instanceFields = {};
    Object.keys(fields).forEach((fieldName) => {
      const field = fields[fieldName];
      if (field._groupName === groupName && field._groupId === instanceId) {
        instanceFields[field._fieldKey] = field;
      }
    });
    return instanceFields;
  };

  const setGroupValidators = (groupName, instanceId, fieldKey, validators) => {
    const fieldName = `${groupName}_${instanceId}_${fieldKey}`;
    setValidators(fieldName, validators);
  };

  return {
    fields,
    getFormValues,
    setField,
    setFieldWithError,
    setFieldValue,
    setValidators,
    setError,
    setErrors,
    resetField,
    updateForm,
    validateField,
    validateForm,
    getErroringFieldNames,
    resetForm,
    // Group methods
    addGroupInstance,
    removeGroupInstance,
    getGroupInstances,
    getGroupInstanceValues,
    getGroupValues,
    getGroupFields,
    setGroupValidators,
  };
}
